/**
 * outlierMark — the Preisspiegel's "unplausibel" marker (HOF-19).
 *
 * An observation whose €/kg falls outside its crop's declared `eur_per_kg_bands`
 * window arrives from the server carrying an `OutOfBandMark`. This module is how
 * it is said on screen. The policy it renders is the ticket's whole reason for
 * existing, and it has three parts that must all hold at once:
 *
 *  - **Show it.** The point stays plotted, at its own value, in its own
 *    position, and stays clickable through to its source document. The tempting
 *    design — filter it out of the chart, log it as a minor error — is rejected,
 *    and the sharpest of the three reasons is that it hides our own bugs from
 *    our best detector: the 40,00 €/kg point that sat live from 2024-08-16 was
 *    found because it was VISIBLE.
 *  - **Mark it, legibly without colour.** A dotted ring on the plot, a dotted
 *    outline on the panel row. Not a hue change: a farmer with a colour-vision
 *    deficiency, or a printed chart, must read the same chart everyone else does
 *    — the same argument `estimateMark`'s hatch makes, and `deckungsbeitrag`'s
 *    45° bar hatch before it.
 *  - **Say what produced it.** AC 16: the tooltip names the comparison, both
 *    edges and the unit. Not decoration — a mark with no citable reference is
 *    unshippable on a screen whose whole argument is transparency, and it is
 *    unactionable besides. A farmer looking at a genuine 13,08 €/kg
 *    Direktvermarktung sale to a Gaststätte has to be able to see in two seconds
 *    that the machine compared it against a wholesale-shaped band and is wrong.
 *
 * ── Why there is no chip and nothing to click ────────────────────────────────
 *
 * Antonio's A6 is explicit: no confirmation process, no „prüfen?" chip, no
 * confirm/reject, no stored review state. This is a purely ANALYTICAL
 * instrument. So the mark is inert — a marker and a `title`, nothing that offers
 * a verdict a human is invited to record, because there is nowhere to record it
 * and a control that does nothing is worse than none. The one thing a reader
 * CAN do is drill into the bucket and open the source document, which is a
 * navigation, not a workflow.
 *
 * ── Why "unplausibel" and not a new word ─────────────────────────────────────
 *
 * It is already this product's word for exactly this posture. #2773's DB1 band
 * marks a Deckungsbeitrag row "unplausibel"
 * (`deckungsbeitrag/rowQualityCopy.ts`) and its config states the rule the two
 * share verbatim: *"It never deletes, corrects or suppresses a figure — it marks
 * one"*, inheriting `eur_per_kg_bands`' own posture, *"narrow the reason, never
 * the acceptance"*. A farmer who has learnt what the word means on the
 * Deckungsbeitrag page already knows what it means here, and the spec forbids
 * inventing a new German compound.
 *
 * The SENTENCE mirrors that module's too — "liegt außerhalb des für diese Kultur
 * hinterlegten Erwartungsbereichs" — with the numbers added, because a
 * Deckungsbeitrag row's band is a plausibility tripwire the reader is not
 * expected to argue with, and a price on this page is one they must be able to.
 */
import './outlierMark.css';
import type { OutOfBandMark } from './types';

/** The marker's word. One string, every surface — the explanation rides in ``title``. */
export const OUT_OF_BAND_LABEL = 'unplausibel';

/**
 * German decimal formatting for a price inside the tooltip.
 *
 * Two decimals, matching every other price on this page. The band edges are
 * whole numbers in the config today (1,0 / 40,0) and are formatted the same way
 * regardless, so a future 0,85 edge does not render as "0,9" beside a value
 * printed to the cent.
 */
function fmt(value: number): string {
  return value.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * The whole explanation, in one `title` string.
 *
 * Four things, in the order a reader needs them:
 *
 *  1. **The value, in the unit it was COMPARED in.** Not the unit it was
 *     published in — those differ for a €/dt cereal price, and printing the
 *     published number beside a €/kg band is the unit mismatch this page exists
 *     to catch, committed by the copy.
 *  2. **The crop and both edges**, so the comparison is checkable rather than
 *     asserted. This is AC 16's "which comparison, in which unit".
 *  3. **The consequence** — shown, but not in the average. Stated because it is
 *     surprising: every other number on this chart is in the number above it.
 *  4. **The published unit, only when it differs.** Otherwise the sentence would
 *     end with a parenthetical restating what it just said, which reads as a
 *     second, different fact.
 */
export function outOfBandTitle(mark: OutOfBandMark): string {
  const converted = mark.publishedUnit === mark.unit ? '' : ` Veröffentlicht wurde er als ${mark.publishedUnit}-Preis.`;
  return (
    `Dieser Preis liegt mit ${fmt(mark.value)} ${mark.unit} außerhalb des für ${mark.crop} ` +
    `hinterlegten Erwartungsbereichs von ${fmt(mark.bandMin)}–${fmt(mark.bandMax)} ${mark.unit}. ` +
    `Er wird angezeigt, fließt aber nicht in den mengengewichteten Durchschnitt ein.${converted}`
  );
}

/**
 * The sentence for when the plausibility check itself could not run.
 *
 * The one clause on the chart footer that fires when there is NOTHING to see,
 * and that inversion is the point. Every other clause describes a glyph on the
 * plot and is suppressed when the glyph is absent. This one describes the
 * absence of a glyph, because an unmarked chart is exactly what a healthy
 * in-band tenant looks like — so without it a failed check and a clean chart
 * read identically, which is the ambiguity the server flag exists to close.
 *
 * It says what was NOT done rather than what might be wrong: nothing here is
 * evidence about the prices, only about the check.
 */
export const OUT_OF_BAND_UNAVAILABLE_CAPTION =
  'Die Plausibilitätsprüfung konnte für diese Ansicht nicht durchgeführt werden — hier ist nichts als geprüft ' +
  'markiert und nichts vom Durchschnitt ausgenommen.';

/**
 * The extra half-sentence for a bucket where NOTHING was left to average.
 *
 * A separate string rather than a branch inside {@link outOfBandTitle}, because
 * it is a different claim about a different subject: the first says this
 * observation left the average, this one says the drawn number has no
 * unmarked observation behind it at all. Reachable and not rare — HOF-17's
 * artefact sat in a channel series with exactly one member — and it is the one
 * case where a reader could otherwise take the plotted value as vouched for.
 */
export const OUT_OF_BAND_ALL_EXCLUDED_TITLE =
  'Für diesen Punkt gibt es keine Beobachtung innerhalb des Erwartungsbereichs — der angezeigte Wert ist der ' +
  'beobachtete, nicht ein geprüfter Durchschnitt.';


/**
 * The inert marker beside a price in the day panel.
 *
 * Deliberately no ``aria-describedby`` and deliberately not a `<button>`: the
 * visible word carries the signal, ``title`` carries the explanation, and there
 * is nothing to press. Matches `EstimatedChip`, which made the same two choices
 * for the same reasons (codex declined the `aria-describedby` indirection on the
 * DB table badge in PR #2752).
 */
export function OutOfBandMarker({ mark }: { mark: OutOfBandMark }) {
  return (
    <span className="preisspiegel-outlier-marker" title={outOfBandTitle(mark)}>
      {OUT_OF_BAND_LABEL}
    </span>
  );
}
